/**
 * Pagination and cache configuration constants for request tables
 *
 * These constants are used across all request tables (Customer, SuperAdmin, Designer, Team Lead)
 * to ensure consistent pagination behavior and caching strategy.
 */

/**
 * Default pagination settings for request tables
 */
export const PAGINATION_DEFAULTS = {
  /** Default number of items per page */
  DEFAULT_ITEMS_PER_PAGE: 10,

  /** Default page number when table first loads */
  DEFAULT_PAGE: 1,

  /** Available page size options for user selection */
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

/**
 * React Query cache configuration for request data
 *
 * These values control how long data is considered fresh and when it should be garbage collected.
 * Balances between reducing server load and keeping data up-to-date.
 */
export const CACHE_CONFIG = {
  /** How long unused data is kept in cache before garbage collection (30 minutes) */
  GC_TIME: 30 * 60 * 1000,

  /** Auto-refetch interval when browser tab is visible (2 minutes) */
  REFETCH_INTERVAL: 2 * 60 * 1000,

  /** How long data is considered fresh before refetch (10 minutes) */
  STALE_TIME: 10 * 60 * 1000,
} as const;

/**
 * Default sort configurations for different table contexts
 */
export const DEFAULT_SORT = {
  /** Default sort for completed requests */
  COMPLETED_DATE_DESC: {
    column: "completedDate",
    direction: "descending" as const,
  },

  /** Default sort for most tables - newest first */
  CREATED_AT_DESC: {
    column: "createdAt",
    direction: "descending" as const,
  },

  /** Default sort for due date tables - soonest first */
  DUE_DATE_ASC: {
    column: "dueDate",
    direction: "ascending" as const,
  },

  /** Default sort for overdue tables - most overdue first */
  DUE_DATE_ASC_OVERDUE: {
    column: "dueDate",
    direction: "ascending" as const,
  },
} as const;
